import { FC, useState } from 'react';
import { BoxV2 as Box, usePermission, Flex } from 'portal-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons';
import { faSquareCheck, faSquareX } from '@fortawesome/pro-light-svg-icons';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { Menu } from '@/components';
import { VettingScoreTooltip } from '..';
import { CampaignOperation } from '@/apis/core/types';
import { formatTitleCase } from '../../utils';
import { RcsCarrierAction } from './types';
import { toastFlashMessage } from '@/utils';

export interface Carrier {
  mnoId: number;
  mno: string;
  qualifying: boolean;
  mnoReview: boolean | null;
  campaignStatus: CampaignOperation['status'];
  sharingStatus: string;
  electedDca: string | null;
  verification?: {
    status?: string | null;
    vettingId?: string | null;
    vettingToken?: string | null;
    reasons?: string[];
  };
  directCarrier: boolean;
}

interface CarrierStatusTableProps {
  data: Carrier[];
  onActionClick?: (
    mnoInfo: { mnoId: Carrier['mnoId']; mno: Carrier['mno'] },
    action: RcsCarrierAction
  ) => void;
  dca2?: boolean; // indicate if this is a DCA2 campaign
  campaignExpired: boolean;
}

const ValidityIcon = ({ valid }: { valid: boolean }) => {
  return (
    <FontAwesomeIcon
      fontSize="16px"
      icon={valid ? faSquareCheck : faSquareX}
      color={valid ? '#000' : '#999FA0'}
    />
  );
};

const CarrierStatusTable: FC<CarrierStatusTableProps> = ({
  data = [],
  onActionClick,
  dca2 = false,
  campaignExpired,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [activeMno, setActiveMno] = useState<string | null>(null);
  const { hasPermission } = usePermission();

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    mno: string
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setActiveMno(mno);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setActiveMno(null);
  };

  const getMenuItems = (props: {
    sharingStatus: string;
    campaignStatus: Carrier['campaignStatus'];
    isDirectCarrier: boolean;
    electedDca: string | null;
  }): Array<{
    label: string;
    value: string;
    disabled: boolean;
  }> => {
    const { sharingStatus, campaignStatus, isDirectCarrier, electedDca } =
      props;
    const items = [
      ...(!dca2
        ? [
            {
              value: 'electDca',
              label: 'Select DCA',
              visible: !sharingStatus && !electedDca,
              disabled:
                !hasPermission('rcsCampaignDetail.electDca') || campaignExpired,
            },
            {
              value: 'updateDca',
              label: 'Update DCA',
              visible: !sharingStatus
                ? isDirectCarrier && campaignStatus === 'REJECTED'
                : ['ACCEPTED', 'DECLINED'].includes(sharingStatus),
              disabled:
                !hasPermission('rcsCampaignDetail.updateDca') ||
                campaignExpired,
            },
          ]
        : []),

      // Status update items for accepted sharing status
      ...(sharingStatus === 'ACCEPTED' && isDirectCarrier
        ? [
            {
              value: campaignStatus === 'INACTIVE' ? 'activate' : 'unsuspend',
              label: 'Update status to active',
              visible:
                !!campaignStatus &&
                ['INACTIVE', 'REJECTED', 'SUSPENDED'].includes(campaignStatus),
              disabled:
                !hasPermission('rcsCampaignDetail.activateCarrier') ||
                campaignExpired,
            },
            {
              value: 'reject',
              label: 'Update status to rejected',
              visible:
                !!campaignStatus &&
                ['INACTIVE', 'ACTIVE', 'SUSPENDED'].includes(campaignStatus),
              disabled:
                !hasPermission('rcsCampaignDetail.rejectCarrier') ||
                campaignExpired,
            },
            {
              value: 'suspend',
              label: 'Update status to suspended',
              visible: campaignStatus === 'ACTIVE',
              disabled:
                !hasPermission('rcsCampaignDetail.suspendCarrier') ||
                campaignExpired,
            },
            {
              value: 'terminate',
              label: 'Update status to terminated',
              visible:
                campaignStatus === 'ACTIVE' || campaignStatus === 'SUSPENDED',
              disabled:
                !hasPermission('rcsCampaignDetail.terminateCarrier') ||
                campaignExpired,
            },
          ]
        : []),
    ]
      .filter((item) => item.visible)
      .map(({ label, value, disabled }) => ({ label, value, disabled }));

    return items;
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <Box
        as="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          backgroundColor: 'white',
          height: 'auto',
        }}
        data-testid="carrierStatusTable"
      >
        <Box as="thead" sx={{ backgroundColor: '#105F83' }}>
          <Box as="tr">
            <TableHeader isLast={false}>MNO</TableHeader>
            <TableHeader isLast={false}>Qualifying</TableHeader>
            <TableHeader isLast={false}>Campaign Status</TableHeader>
            {!dca2 && (
              <>
                <TableHeader isLast={false}>Sharing Status</TableHeader>
                <TableHeader isLast={false}>Elected DCA</TableHeader>
              </>
            )}
            <TableHeader isLast={false}>Verification</TableHeader>
            <TableHeader isLast={true}>Action</TableHeader>
          </Box>
        </Box>
        <Box
          as="tbody"
          sx={{
            tr: {
              borderBottom: '1px solid',
              borderColor: '#E6E7E7',
              '&:last-child': { borderBottom: 'none' },
            },
            td: {
              padding: '11px 10px 11px 16px',
              fontSize: 'H300',
              color: '#101820',
            },
          }}
        >
          {data.map(
            ({
              mno,
              mnoId,
              qualifying,
              campaignStatus,
              sharingStatus,
              electedDca,
              verification,
              directCarrier,
            }) => {
              const menuItems = getMenuItems({
                sharingStatus,
                campaignStatus,
                isDirectCarrier: directCarrier,
                electedDca,
              });
              return (
                <tr key={mno}>
                  <td data-testid="carrierStatusTableMnoName">{mno}</td>
                  <td data-testid="carrierStatusTableQualifying">
                    <ValidityIcon valid={qualifying} />
                  </td>
                  <td data-testid="carrierStatusTableCampaignStatus">
                    {campaignStatus ? formatTitleCase(campaignStatus) : 'N/A'}
                  </td>
                  {!dca2 && (
                    <>
                      <td data-testid="carrierStatusTableSharingStatus">
                        {formatTitleCase(sharingStatus) || 'N/A'}
                      </td>
                      <td data-testid="carrierStatusTableElectedDca">
                        {electedDca || 'N/A'}
                      </td>
                    </>
                  )}

                  <td data-testid="carrierStatusTableVerification">
                    <Flex sx={{ gap: 'xs', alignItems: 'center' }}>
                      {verification?.status
                        ? formatTitleCase(verification?.status)
                        : 'N/A'}
                      {(!!verification?.vettingId ||
                        !!verification?.vettingToken) && (
                        <VettingScoreTooltip
                          data={verification.reasons || []}
                          interactive
                          placement="left-start"
                          footer={
                            <CopyableVettingIdToken
                              vettingId={verification.vettingId}
                              vettingToken={verification.vettingToken}
                            />
                          }
                          popperSx={{
                            left: '0',
                          }}
                        />
                      )}
                    </Flex>
                  </td>
                  <td>
                    {menuItems.length > 0 && (
                      <>
                        <Box
                          role="button"
                          onClick={(e) => handleMenuClick(e, mno)}
                          sx={{ cursor: 'pointer' }}
                          data-testid="carrierStatusTableActionButton"
                        >
                          <FontAwesomeIcon icon={faEllipsisVertical} />
                        </Box>
                        <Menu
                          open={activeMno === mno}
                          anchorEl={menuAnchorEl}
                          onClose={handleMenuClose}
                          onItemClick={(value) =>
                            onActionClick?.(
                              { mnoId, mno },
                              value as RcsCarrierAction
                            )
                          }
                          data-testid="carrierStatusTableActionButton"
                        >
                          {menuItems.map((item) => (
                            <Menu.MenuItem
                              key={item.value}
                              value={item.value}
                              disabled={item.disabled}
                            >
                              {item.label}
                            </Menu.MenuItem>
                          ))}
                        </Menu>
                      </>
                    )}
                  </td>
                </tr>
              );
            }
          )}
        </Box>
      </Box>
    </Box>
  );
};

const TableHeader: FC<{ isLast: boolean }> = ({ children, isLast }) => {
  return (
    <Box
      as="th"
      sx={{
        padding: '12px 8px 8px 16px',
        textAlign: 'left',
        color: 'white',
        fontWeight: 'normal',
        whiteSpace: 'nowrap',
        pl: isLast ? '8px' : undefined,
      }}
    >
      {children}
    </Box>
  );
};

const CopyableVettingIdToken = ({ vettingId, vettingToken }) => {
  return (
    <Flex
      sx={{
        alignItems: 'center',
        gap: '27px',
        fontSize: 'H200',
        width: '100%',
        py: 's',
        '[role="button"]': {
          cursor: 'pointer',
          textDecoration: 'underline',
          color: 't.blue100',
          marginLeft: '4px',
        },
        '> *': {
          flex: '1',
        },
      }}
    >
      <VettingField
        label="Vetting ID"
        value={vettingId}
        successMsg="Vetting ID has been copied to your clipboard"
        buttonTestId="vettingIdCopyButton"
      />

      <VettingField
        label="Vetting Token"
        value={vettingToken}
        successMsg="Vetting Token has been copied to your clipboard"
        buttonTestId="vettingTokenCopyButton"
      />
    </Flex>
  );
};

const VettingField = ({ label, value, successMsg, buttonTestId }) => {
  return (
    <Flex>
      <Box as="label" sx={{ minWidth: 'fit-content', maxWidth: 'fit-content' }}>
        {label}:
      </Box>
      <Box
        sx={{
          flexShrink: '1',
          flexBasis: 'auto',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          ml: '6px',
        }}
      >
        {value || 'N/A'}
      </Box>
      {!!value?.length && (
        <CopyToClipboard
          text={value}
          onCopy={(_: string, success: boolean) => {
            success && toastFlashMessage(successMsg, 'success');
          }}
        >
          <div role="button" data-testid={buttonTestId}>
            copy
          </div>
        </CopyToClipboard>
      )}
    </Flex>
  );
};

export default CarrierStatusTable;
